import { 
  doc, 
  getDoc, 
  updateDoc, 
  Timestamp,
  collection,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { getDB } from '../config/firebase';
import { safeTimestampToDate } from '../utils/timestampUtils';

export interface LegacyStudentData {
  id: number;
  name: string;
  attendance: {
    date: string;
    status: 'Present' | 'Absent';
  }[];
}

export interface ModernAttendanceData {
  [date: string]: {
    date: string;
    markedBy: string;
    markedByName: string;
    markedAt: any;
    students: {
      id: string;
      name: string;
      rollNumber: string;
      status: 'Present' | 'Absent';
      remarks: string;
    }[];
    presentCount: number;
    absentCount: number;
    attendanceRate: number;
  };
}

export class AttendanceDataMigrationService {
  /**
   * Convert legacy student-based attendance data to modern date-based structure
   */
  static async migrateLegacySubjectData(subjectId: string, facultyId: string = 'system', facultyName: string = 'System Migration'): Promise<void> {
    try {
      const db = getDB();
      const subjectRef = doc(db, 'subjects', subjectId);
      const subjectDoc = await getDoc(subjectRef);
      
      if (!subjectDoc.exists()) {
        throw new Error('Subject not found');
      }

      const subjectData = subjectDoc.data();
      const students = subjectData.students as LegacyStudentData[];
      
      if (!students || students.length === 0) {
        console.log('No students found in subject');
        return;
      }

      // Check if already migrated
      if (subjectData.attendance && typeof subjectData.attendance === 'object') {
        console.log('Subject already has modern attendance structure');
        return;
      }

      // Group attendance by date
      const attendanceByDate: { [date: string]: any[] } = {};
      
      students.forEach((student) => {
        if (student.attendance && Array.isArray(student.attendance)) {
          student.attendance.forEach((record) => {
            if (!attendanceByDate[record.date]) {
              attendanceByDate[record.date] = [];
            }
            attendanceByDate[record.date].push({
              id: student.id.toString(),
              name: student.name,
              rollNumber: student.id.toString(), // Using ID as roll number if not available
              status: record.status,
              remarks: ''
            });
          });
        }
      });

      // Convert to modern structure
      const modernAttendance: ModernAttendanceData = {};
      
      Object.entries(attendanceByDate).forEach(([date, studentRecords]) => {
        const presentCount = studentRecords.filter(s => s.status === 'Present').length;
        const totalCount = studentRecords.length;
        const attendanceRate = totalCount > 0 ? (presentCount / totalCount) * 100 : 0;

        modernAttendance[date] = {
          date,
          markedBy: facultyId,
          markedByName: facultyName,
          markedAt: Timestamp.fromDate(new Date(date + 'T12:00:00')),
          students: studentRecords,
          presentCount,
          absentCount: totalCount - presentCount,
          attendanceRate: Math.round(attendanceRate * 100) / 100
        };
      });

      // Update subject with modern structure
      await updateDoc(subjectRef, {
        attendance: modernAttendance,
        lastUpdated: Timestamp.fromDate(new Date()),
        migrated: true,
        migratedAt: Timestamp.fromDate(new Date())
      });

      console.log(`Successfully migrated subject ${subjectId} with ${Object.keys(modernAttendance).length} dates`);
    } catch (error) {
      console.error('Error migrating legacy subject data:', error);
      throw error;
    }
  }

  /**
   * Read attendance data from legacy format and convert to expected format
   */
  static async readLegacySubjectAttendance(
    subjectId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    subjectId: string;
    subjectName: string;
    subjectCode: string;
    attendanceData: {
      date: string;
      presentCount: number;
      absentCount: number;
      attendanceRate: number;
      markedBy: string;
      markedByName: string;
      markedAt: Date;
      students: {
        id: string;
        name: string;
        rollNumber: string;
        status: 'Present' | 'Absent';
        remarks: string;
      }[];
    }[];
    totalSessions: number;
    averageAttendanceRate: number;
  }> {
    try {
      const db = getDB();
      const subjectDoc = await getDoc(doc(db, 'subjects', subjectId));
      
      if (!subjectDoc.exists()) {
        throw new Error('Subject not found');
      }

      const subjectData = subjectDoc.data();
      const students = subjectData.students as LegacyStudentData[];
      
      // Check if it's already in modern format
      if (subjectData.attendance && typeof subjectData.attendance === 'object' && !Array.isArray(subjectData.attendance)) {
        // Use modern format reading
        const attendance = subjectData.attendance;
        let filteredAttendance = Object.entries(attendance);
        
        if (startDate) {
          filteredAttendance = filteredAttendance.filter(([date]) => date >= startDate);
        }
        
        if (endDate) {
          filteredAttendance = filteredAttendance.filter(([date]) => date <= endDate);
        }

        filteredAttendance.sort(([a], [b]) => a.localeCompare(b));

        const attendanceData = filteredAttendance.map(([date, record]: [string, any]) => ({
          date,
          presentCount: record.presentCount || 0,
          absentCount: record.absentCount || 0,
          attendanceRate: record.attendanceRate || 0,
          markedBy: record.markedBy || '',
          markedByName: record.markedByName || '',
          markedAt: safeTimestampToDate(record.markedAt),
          students: record.students || []
        }));

        const totalSessions = attendanceData.length;
        const averageAttendanceRate = totalSessions > 0 
          ? attendanceData.reduce((sum, session) => sum + session.attendanceRate, 0) / totalSessions
          : 0;

        return {
          subjectId,
          subjectName: subjectData.name || '',
          subjectCode: subjectData.code || '',
          attendanceData,
          totalSessions,
          averageAttendanceRate: Math.round(averageAttendanceRate * 100) / 100
        };
      }

      // Handle legacy format
      if (!students || students.length === 0) {
        return {
          subjectId,
          subjectName: subjectData.name || '',
          subjectCode: subjectData.code || '',
          attendanceData: [],
          totalSessions: 0,
          averageAttendanceRate: 0
        };
      }

      // Convert legacy format on the fly
      const attendanceByDate: { [date: string]: any[] } = {};
      
      students.forEach((student) => {
        if (student.attendance && Array.isArray(student.attendance)) {
          student.attendance.forEach((record) => {
            if (!attendanceByDate[record.date]) {
              attendanceByDate[record.date] = [];
            }
            attendanceByDate[record.date].push({
              id: student.id.toString(),
              name: student.name,
              rollNumber: student.id.toString(),
              status: record.status,
              remarks: ''
            });
          });
        }
      });

      // Filter by date range
      let filteredDates = Object.keys(attendanceByDate);
      if (startDate) {
        filteredDates = filteredDates.filter(date => date >= startDate);
      }
      if (endDate) {
        filteredDates = filteredDates.filter(date => date <= endDate);
      }
      filteredDates.sort();

      const attendanceData = filteredDates.map(date => {
        const studentRecords = attendanceByDate[date];
        const presentCount = studentRecords.filter(s => s.status === 'Present').length;
        const totalCount = studentRecords.length;
        const attendanceRate = totalCount > 0 ? (presentCount / totalCount) * 100 : 0;

        return {
          date,
          presentCount,
          absentCount: totalCount - presentCount,
          attendanceRate: Math.round(attendanceRate * 100) / 100,
          markedBy: '',
          markedByName: 'Legacy Data',
          markedAt: new Date(date + 'T12:00:00'),
          students: studentRecords
        };
      });

      const totalSessions = attendanceData.length;
      const averageAttendanceRate = totalSessions > 0 
        ? attendanceData.reduce((sum, session) => sum + session.attendanceRate, 0) / totalSessions
        : 0;

      return {
        subjectId,
        subjectName: subjectData.name || '',
        subjectCode: subjectData.code || '',
        attendanceData,
        totalSessions,
        averageAttendanceRate: Math.round(averageAttendanceRate * 100) / 100
      };

    } catch (error) {
      console.error('Error reading legacy subject attendance:', error);
      throw error;
    }
  }

  /**
   * Get student attendance from legacy format
   */
  static async readLegacyStudentSubjectAttendance(
    subjectId: string,
    studentId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    studentId: string;
    studentName: string;
    rollNumber: string;
    subjectId: string;
    subjectName: string;
    subjectCode: string;
    totalSessions: number;
    presentSessions: number;
    attendanceRate: number;
    attendanceRecords: {
      date: string;
      status: 'Present' | 'Absent';
      remarks: string;
      markedBy: string;
      markedByName: string;
    }[];
  }> {
    try {
      const subjectAttendance = await this.readLegacySubjectAttendance(subjectId, startDate, endDate);

      // Find student's attendance records
      const studentRecords: any[] = [];
      let studentName = '';
      let rollNumber = '';

      subjectAttendance.attendanceData.forEach(session => {
        const studentRecord = session.students.find(s => s.id === studentId || s.id === parseInt(studentId).toString());
        if (studentRecord) {
          studentName = studentRecord.name;
          rollNumber = studentRecord.rollNumber;
          studentRecords.push({
            date: session.date,
            status: studentRecord.status,
            remarks: studentRecord.remarks,
            markedBy: session.markedBy,
            markedByName: session.markedByName
          });
        }
      });

      const totalSessions = studentRecords.length;
      const presentSessions = studentRecords.filter(r => r.status === 'Present').length;
      const attendanceRate = totalSessions > 0 ? (presentSessions / totalSessions) * 100 : 0;

      return {
        studentId,
        studentName,
        rollNumber,
        subjectId: subjectAttendance.subjectId,
        subjectName: subjectAttendance.subjectName,
        subjectCode: subjectAttendance.subjectCode,
        totalSessions,
        presentSessions,
        attendanceRate: Math.round(attendanceRate * 100) / 100,
        attendanceRecords: studentRecords
      };

    } catch (error) {
      console.error('Error reading legacy student subject attendance:', error);
      throw error;
    }
  }

  /**
   * Migrate all subjects to modern format
   */
  static async migrateAllSubjects(): Promise<{
    totalSubjects: number;
    migratedSubjects: number;
    errors: string[];
  }> {
    try {
      const db = getDB();
      const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
      
      let totalSubjects = 0;
      let migratedSubjects = 0;
      const errors: string[] = [];

      for (const subjectDoc of subjectsSnapshot.docs) {
        totalSubjects++;
        try {
          const subjectData = subjectDoc.data();
          
          // Skip if already migrated
          if (subjectData.migrated) {
            continue;
          }

          await this.migrateLegacySubjectData(subjectDoc.id);
          migratedSubjects++;
        } catch (error) {
          errors.push(`Error migrating subject ${subjectDoc.id}: ${error}`);
        }
      }

      return {
        totalSubjects,
        migratedSubjects,
        errors
      };
    } catch (error) {
      console.error('Error migrating all subjects:', error);
      throw error;
    }
  }
} 